import { ReleaseType } from '@ibk/shared-types';
import { NewRechargeStep } from '../../types';
const toNumber = (value) => value ?? 0;
const toText = (value) => value ?? '';
export function cloneSummaryEditSnapshot(formData) {
    return {
        ...formData,
        selectedBeneficiariesList: formData.selectedBeneficiariesList?.map(item => ({ ...item })),
        beneficiaries: formData.beneficiaries?.map(item => ({ ...item })),
    };
}
function hasSelectedBeneficiariesImpact(before, after) {
    const beforeList = before ?? [];
    const afterList = after ?? [];
    if (beforeList.length === 0 || afterList.length === 0) {
        return false;
    }
    if (beforeList.length !== afterList.length) {
        return true;
    }
    const afterById = new Map(afterList.map(item => [item.beneficiaryId, item]));
    return beforeList.some(item => {
        const match = afterById.get(item.beneficiaryId);
        if (!match) {
            return true;
        }
        return (toNumber(item.value) !== toNumber(match.value) ||
            toNumber(item.daysQuantity) !== toNumber(match.daysQuantity) ||
            toNumber(item.discountValue) !== toNumber(match.discountValue));
    });
}
function hasBeneficiariesValueImpact(before, after) {
    const beforeList = before ?? [];
    const afterList = after ?? [];
    if (beforeList.length === 0 || afterList.length === 0) {
        return false;
    }
    const afterById = new Map(afterList.map(item => [item.id, item]));
    return beforeList.some(item => {
        const match = afterById.get(item.id);
        if (!match) {
            return false;
        }
        return (toNumber(item.productValue) !== toNumber(match.productValue) ||
            toNumber(item.productDaysQuantity) !== toNumber(match.productDaysQuantity) ||
            toNumber(item.productDiscountValue) !== toNumber(match.productDiscountValue));
    });
}
function hasBusinessDaysImpact(snapshot, current) {
    if (current.releaseType !== ReleaseType.DAILY) {
        return false;
    }
    return toNumber(snapshot.businessDays) !== toNumber(current.businessDays);
}
export function hasRechargeCalculationImpact(snapshot, current) {
    return (snapshot.releaseType !== current.releaseType ||
        toText(snapshot.globalValue) !== toText(current.globalValue) ||
        toNumber(snapshot.amountDaily) !== toNumber(current.amountDaily) ||
        hasBusinessDaysImpact(snapshot, current) ||
        snapshot.definedByBeneficiary !== current.definedByBeneficiary ||
        snapshot.payrollDeduction !== current.payrollDeduction ||
        snapshot.discountType !== current.discountType ||
        toText(snapshot.discountValue) !== toText(current.discountValue) ||
        snapshot.isIndividualDiscount !== current.isIndividualDiscount ||
        hasSelectedBeneficiariesImpact(snapshot.selectedBeneficiariesList, current.selectedBeneficiariesList) ||
        hasBeneficiariesValueImpact(snapshot.beneficiaries, current.beneficiaries));
}
export function canKeepCardDeliveryDecision(isEditingFromSummary, snapshot, current) {
    return (isEditingFromSummary &&
        snapshot !== null &&
        snapshot.voucherTypeId === current.voucherTypeId &&
        !hasRechargeCalculationImpact(snapshot, current));
}
export function shouldSkipDeliveryAddress(isEmissorFlow, requiresCardDelivery) {
    return !isEmissorFlow && requiresCardDelivery === false;
}
export function getNextRechargeStep(currentStep, isEmissorFlow, skipDeliveryAddress = false) {
    switch (currentStep) {
        case NewRechargeStep.COMPETENCE:
            return isEmissorFlow ? NewRechargeStep.PRODUCTS : NewRechargeStep.GLOBAL_SETTINGS;
        case NewRechargeStep.PRODUCTS:
            return NewRechargeStep.GLOBAL_SETTINGS;
        case NewRechargeStep.GLOBAL_SETTINGS:
            return NewRechargeStep.BENEFICIARIES_VALUES;
        case NewRechargeStep.BENEFICIARIES_VALUES:
            return skipDeliveryAddress ? NewRechargeStep.SUMMARY : NewRechargeStep.DELIVERY_ADDRESS;
        case NewRechargeStep.DELIVERY_ADDRESS:
            return NewRechargeStep.SUMMARY;
        case NewRechargeStep.SUMMARY:
            return NewRechargeStep.PAYMENT;
        case NewRechargeStep.PAYMENT:
            return NewRechargeStep.SUMMARY;
        default: {
            currentStep;
            return NewRechargeStep.SUMMARY;
        }
    }
}
export function getPreviousRechargeStep(currentStep, isEmissorFlow, skipDeliveryAddress = false) {
    switch (currentStep) {
        case NewRechargeStep.PAYMENT:
            return NewRechargeStep.SUMMARY;
        case NewRechargeStep.SUMMARY:
            return skipDeliveryAddress ? NewRechargeStep.BENEFICIARIES_VALUES : NewRechargeStep.DELIVERY_ADDRESS;
        case NewRechargeStep.DELIVERY_ADDRESS:
            return NewRechargeStep.BENEFICIARIES_VALUES;
        case NewRechargeStep.BENEFICIARIES_VALUES:
            return NewRechargeStep.GLOBAL_SETTINGS;
        case NewRechargeStep.GLOBAL_SETTINGS:
            return isEmissorFlow ? NewRechargeStep.PRODUCTS : NewRechargeStep.COMPETENCE;
        case NewRechargeStep.PRODUCTS:
            return NewRechargeStep.COMPETENCE;
        case NewRechargeStep.COMPETENCE:
            return NewRechargeStep.BENEFICIARIES_VALUES;
        default: {
            currentStep;
            return NewRechargeStep.BENEFICIARIES_VALUES;
        }
    }
}
export function resolveAdvanceStep({ currentStep, isEmissorFlow, explicitNextStep, isEditingFromSummary, snapshot, currentFormData, skipDeliveryAddress = false, }) {
    const isEmissorProductSwitch = isEmissorFlow &&
        currentStep === NewRechargeStep.PRODUCTS &&
        snapshot !== null &&
        snapshot.voucherTypeId !== currentFormData.voucherTypeId;
    const calculationImpacted = isEditingFromSummary &&
        snapshot !== null &&
        !isEmissorProductSwitch &&
        hasRechargeCalculationImpact(snapshot, currentFormData);
    if (isEditingFromSummary && !calculationImpacted) {
        return NewRechargeStep.SUMMARY;
    }
    const shouldIgnoreSummarySkip = isEditingFromSummary && calculationImpacted && explicitNextStep === NewRechargeStep.SUMMARY;
    if (explicitNextStep && !shouldIgnoreSummarySkip) {
        return explicitNextStep;
    }
    return getNextRechargeStep(currentStep, isEmissorFlow, skipDeliveryAddress);
}
